import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import {
  BarChart3, RefreshCw, Play, Pause, Users, ArrowDownCircle, ArrowUpCircle,
  Gift, Wallet, TrendingUp, TrendingDown, Clock, Calendar, Building2, Gamepad2,
  AlertTriangle, CheckCircle2, Layers,
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const PERIODS = [
  { id: 'day', label: 'Today', hint: 'Midnight UTC → now, hourly' },
  { id: 'yesterday', label: 'Yesterday', hint: 'Yesterday 00:00 → 23:59' },
  { id: 'week', label: '7 Days', hint: 'Daily breakdown' },
  { id: 'month', label: '30 Days', hint: 'Daily breakdown' },
  { id: '90d', label: '90 Days', hint: 'Daily breakdown' },
];

const POLL_MS = 2000; // near-real-time refresh cadence (2s)
const POOL_POLL_MS = 60000; // provider pool balance refreshes every 60s (upstream is slow)

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtMoney = fmtUSD;
const fmtFP = (v) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} FP`;
const fmtInt = (v) => Number(v || 0).toLocaleString();

function fmtTick(ts, gran) {
  if (!ts) return '';
  if (gran === 'hour') return ts.slice(11, 13) + 'h';
  if (gran === 'month') return ts;
  // day: show MMM DD
  try {
    const d = new Date(ts + 'T00:00:00Z');
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ts; }
}

// Merge timeseries A & B on ts into [{ts, a, b}]
function mergeSeries(a, b) {
  const map = new Map();
  (a || []).forEach((p) => { map.set(p.ts, { ts: p.ts, a: p.amount || 0 }); });
  (b || []).forEach((p) => {
    const row = map.get(p.ts) || { ts: p.ts, a: 0 };
    row.b = p.amount || 0;
    map.set(p.ts, row);
  });
  return Array.from(map.values()).sort((x, y) => x.ts.localeCompare(y.ts));
}

export default function InsightsDashboardPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const basePath = tenantSlug ? `/${tenantSlug}/admin` : '/admin';
  const goto = (tab, query = '') => navigate(`${basePath}/${tab}${query}`);

  const [period, setPeriod] = useState('week');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [rangeActive, setRangeActive] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [liveOn, setLiveOn] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const pollRef = useRef(null);

  // Provider pool balances — master admin only. Fetched on its own slower
  // cadence because each balance hits the upstream API.
  const [poolData, setPoolData] = useState(null);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolDenied, setPoolDenied] = useState(false);
  const poolPollRef = useRef(null);

  const fetchPoolBalances = async () => {
    setPoolLoading(true);
    try {
      const res = await api.get('/api/admin/analytics/provider-pool-balances');
      setPoolData(res.data);
      setPoolDenied(false);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        setPoolDenied(true); // tenant admin — hide the section
      }
    } finally {
      setPoolLoading(false);
    }
  };

  const fetchData = async (p = period, silent = false) => {
    if (!silent) setLoading(true);
    setIsFetching(true);
    try {
      let url = `/api/admin/analytics/insights?period=${p}`;
      if (rangeActive && customRange.start && customRange.end) {
        url = `/api/admin/analytics/insights?start=${encodeURIComponent(customRange.start)}&end=${encodeURIComponent(customRange.end)}`;
      }
      const res = await api.get(url);
      setData(res.data);
      setLastUpdated(new Date());
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.message || 'Failed to load insights');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  // Initial + period-change load
  useEffect(() => { fetchData(period); /* eslint-disable-next-line */ }, [period, rangeActive]);

  // Live polling
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!liveOn) return;
    pollRef.current = setInterval(() => { fetchData(period, true); }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [liveOn, period]);

  // Provider pool balances — fetch once at mount, then poll at a slower rate
  // (upstream is slow; no need to hammer it every 2 seconds).
  useEffect(() => {
    fetchPoolBalances();
    if (poolPollRef.current) { clearInterval(poolPollRef.current); poolPollRef.current = null; }
    if (!liveOn) return;
    poolPollRef.current = setInterval(() => { fetchPoolBalances(); }, POOL_POLL_MS);
    return () => { if (poolPollRef.current) clearInterval(poolPollRef.current); };
    // eslint-disable-next-line
  }, [liveOn]);

  const gran = data?.granularity || 'day';
  const kpis = data?.kpis || {};

  const depWithSeries = useMemo(
    () => mergeSeries(data?.timeseries?.deposits, data?.timeseries?.withdrawals).map((r) => ({
      ts: fmtTick(r.ts, gran), Deposits: r.a || 0, Withdrawals: r.b || 0,
    })),
    [data?.timeseries, gran],
  );

  const signupsSeries = useMemo(
    () => (data?.timeseries?.signups || []).map((p) => ({ ts: fmtTick(p.ts, gran), Signups: p.count || 0 })),
    [data?.timeseries?.signups, gran],
  );

  const bonusSeries = useMemo(
    () => (data?.timeseries?.bonuses || []).map((p) => ({ ts: fmtTick(p.ts, gran), Bonus: p.amount || 0 })),
    [data?.timeseries?.bonuses, gran],
  );

  return (
    <div className="space-y-6" data-testid="insights-dashboard">
      {/* Header / Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#D4AF37]" /> Live Insights
          </h2>
          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" />
            {lastUpdated ? `Last updated ${lastUpdated.toLocaleTimeString()}` : 'Loading…'}
            {liveOn && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[10px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> LIVE
            </span>}
            {isFetching && <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-500" />}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period buttons */}
          <div className="flex rounded-xl bg-[#0B0E14] border border-white/10 p-1" data-testid="period-filter">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => { setPeriod(p.id); setRangeActive(false); setCustomRange({ start: '', end: '' }); }}
                title={p.hint}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  !rangeActive && period === p.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {/* Custom date range */}
          <button
            onClick={() => setShowRangePicker(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition ${
              rangeActive ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'bg-[#0B0E14] text-gray-400 border-white/10 hover:text-white'
            }`}
            data-testid="date-range-btn"
            title="Custom date range"
          >
            <Calendar className="w-3.5 h-3.5" />
            {rangeActive && customRange.start ? `${customRange.start.slice(0,10)} → ${customRange.end.slice(0,10)}` : 'Custom range'}
          </button>
          <button
            onClick={() => setLiveOn(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition ${
              liveOn ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-[#0B0E14] text-gray-400 border-white/10'
            }`}
            title={liveOn ? 'Live updates on — click to pause' : 'Live updates off — click to resume'}
            data-testid="live-toggle"
          >
            {liveOn ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {liveOn ? 'Pause' : 'Live'}
          </button>
          <button
            onClick={() => fetchData(period)}
            className="p-2 rounded-lg bg-[#0B0E14] border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition"
            data-testid="refresh-insights"
            title="Refresh now"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="insights-error">
          {err}
        </div>
      )}

      {/* Custom date range picker */}
      {showRangePicker && (
        <div
          className="rounded-2xl p-4 bg-[#131822] border border-[#D4AF37]/30 flex items-end gap-3 flex-wrap"
          data-testid="date-range-picker"
        >
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Start date</label>
            <input
              type="date"
              value={customRange.start ? customRange.start.slice(0, 10) : ''}
              onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value ? `${e.target.value}T00:00:00.000Z` : '' }))}
              className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
              data-testid="date-range-start"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">End date</label>
            <input
              type="date"
              value={customRange.end ? customRange.end.slice(0, 10) : ''}
              onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value ? `${e.target.value}T23:59:59.999Z` : '' }))}
              className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
              data-testid="date-range-end"
            />
          </div>
          <button
            onClick={() => {
              if (!customRange.start || !customRange.end) return;
              setRangeActive(true);
              setShowRangePicker(false);
            }}
            disabled={!customRange.start || !customRange.end}
            className="px-4 py-1.5 rounded-lg bg-[#D4AF37] text-black text-xs font-semibold hover:bg-[#F5D76E] disabled:opacity-40 transition"
            data-testid="date-range-apply"
          >
            Apply Range
          </button>
          {rangeActive && (
            <button
              onClick={() => { setRangeActive(false); setCustomRange({ start: '', end: '' }); }}
              className="px-4 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-xs hover:text-white transition"
              data-testid="date-range-clear"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      ) : (
        <>
          {/* Period KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI
              testid="kpi-period-deposits"
              icon={ArrowDownCircle}
              label="Deposits"
              subtitle={PERIODS.find(p => p.id === period)?.label}
              primary={fmtUSD(kpis.period_deposit_amount)}
              secondary={`${fmtInt(kpis.period_deposit_count)} txns`}
              color="#22c55e"
            />
            <KPI
              testid="kpi-period-withdrawals"
              icon={ArrowUpCircle}
              label="Withdrawals"
              subtitle={PERIODS.find(p => p.id === period)?.label}
              primary={fmtUSD(kpis.period_withdraw_amount)}
              secondary={`${fmtInt(kpis.period_withdraw_count)} txns`}
              color="#ef4444"
            />
            <KPI
              testid="kpi-period-signups"
              icon={Users}
              label="New Sign-ups"
              subtitle={PERIODS.find(p => p.id === period)?.label}
              primary={fmtInt(kpis.period_signups)}
              color="#3B82F6"
              onClick={() => goto('players', `?filter=new&period=${period}`)}
            />
            <KPI
              testid="kpi-period-bonus"
              icon={Gift}
              label="Bonuses Given"
              subtitle={PERIODS.find(p => p.id === period)?.label}
              primary={fmtFP(kpis.period_bonus_amount)}
              secondary={`${fmtInt(kpis.period_bonus_count)} grants`}
              color="#D4AF37"
            />
          </div>

          {/* Net Revenue + Pending */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI
              testid="kpi-net-revenue"
              icon={kpis.period_net_revenue >= 0 ? TrendingUp : TrendingDown}
              label="Net Revenue"
              subtitle="deposits − withdrawals"
              primary={fmtUSD(kpis.period_net_revenue)}
              color={kpis.period_net_revenue >= 0 ? '#22c55e' : '#ef4444'}
            />
            <KPI
              testid="kpi-pending-deposits"
              icon={Clock}
              label="Pending Deposits"
              primary={fmtInt(kpis.pending_deposit_count)}
              secondary={fmtUSD(kpis.pending_deposit_amount)}
              color="#eab308"
              urgent={kpis.pending_deposit_count > 0}
              onClick={() => goto('deposit-requests', '?status=pending')}
            />
            <KPI
              testid="kpi-pending-withdrawals"
              icon={Clock}
              label="Pending Withdrawals"
              primary={fmtInt(kpis.pending_withdraw_count)}
              secondary={fmtUSD(kpis.pending_withdraw_amount)}
              color="#f97316"
              urgent={kpis.pending_withdraw_count > 0}
              onClick={() => goto('withdrawal-requests', '?status=pending')}
            />
            <KPI
              testid="kpi-total-players"
              icon={Users}
              label="Total Players"
              subtitle="lifetime"
              primary={fmtInt(kpis.lifetime_player_count)}
              color="#8b5cf6"
              onClick={() => goto('players')}
            />
          </div>

          {/* Lifetime Totals Strip */}
          <div className="rounded-2xl p-5 bg-gradient-to-br from-[#1a0f2e] to-[#0B0E14] border border-white/10" data-testid="lifetime-totals">
            <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#D4AF37]" /> Lifetime Totals
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <LifetimeStat
                testid="lifetime-deposits"
                icon={ArrowDownCircle}
                label="Total Deposits"
                amount={fmtUSD(kpis.lifetime_deposit_amount)}
                count={`${fmtInt(kpis.lifetime_deposit_count)} transactions`}
                color="#22c55e"
              />
              <LifetimeStat
                testid="lifetime-withdrawals"
                icon={ArrowUpCircle}
                label="Total Withdrawals"
                amount={fmtUSD(kpis.lifetime_withdraw_amount)}
                count={`${fmtInt(kpis.lifetime_withdraw_count)} transactions`}
                color="#ef4444"
              />
              <LifetimeStat
                testid="lifetime-players"
                icon={Users}
                label="Total Players"
                amount={fmtInt(kpis.lifetime_player_count)}
                count={`across the platform`}
                color="#8b5cf6"
              />
            </div>
          </div>

          {/* Provider Pool Balances (master admin only) */}
          {!poolDenied && (
            <ProviderPoolBalances
              data={poolData}
              loading={poolLoading}
              onRefresh={fetchPoolBalances}
            />
          )}

          {/* Deposits vs Withdrawals Chart */}
          <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="chart-deposits-withdrawals">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#D4AF37]" /> Deposits vs Withdrawals
              </h3>
              <span className="text-[11px] text-gray-500">{PERIODS.find(p => p.id === period)?.label} · {gran === 'hour' ? 'Hourly' : 'Daily'}</span>
            </div>
            {depWithSeries.length === 0 ? (
              <EmptyChart label="No deposit or withdrawal activity in this period" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={depWithSeries}>
                  <defs>
                    <linearGradient id="gDep" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="gWith" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtUSD(v)} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                  <Area type="monotone" dataKey="Deposits" stroke="#22c55e" fill="url(#gDep)" strokeWidth={2} />
                  <Area type="monotone" dataKey="Withdrawals" stroke="#ef4444" fill="url(#gWith)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Signups + Bonuses — two smaller charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="chart-signups">
              <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#3B82F6]" /> New Sign-ups
              </h3>
              {signupsSeries.length === 0 ? (
                <EmptyChart label="No sign-ups in this period" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={signupsSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                    <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="Signups" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="chart-bonus">
              <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                <Gift className="w-4 h-4 text-[#D4AF37]" /> Bonuses Given (FP)
              </h3>
              {bonusSeries.length === 0 ? (
                <EmptyChart label="No bonuses granted in this period" />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={bonusSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                    <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#ffffff10' }} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtFP(v)} />
                    <Line type="monotone" dataKey="Bonus" stroke="#D4AF37" strokeWidth={2.4} dot={{ fill: '#D4AF37', r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Payment-provider breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ProviderBreakdown
              testid="breakdown-payment-deposits"
              icon={Wallet}
              title="Deposits by Payment Provider"
              color="#22c55e"
              rows={data?.by_payment_provider || []}
              amountPrefix="$"
              emptyLabel="No approved deposits in this period"
            />
            <ProviderBreakdown
              testid="breakdown-payment-withdrawals"
              icon={Wallet}
              title="Withdrawals by Payment Provider"
              color="#ef4444"
              rows={data?.by_payment_provider_withdrawals || []}
              amountPrefix="$"
              emptyLabel="No approved withdrawals in this period"
            />
          </div>

          {/* Game-provider breakdown */}
          <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="breakdown-game-provider">
            <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <Gamepad2 className="w-4 h-4 text-[#8b5cf6]" /> Game Providers — Loads & Redeems
            </h3>
            {(data?.by_game_provider || []).length === 0 ? (
              <EmptyChart label="No game activity in this period" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/5">
                      <th className="py-2 pr-4">Provider</th>
                      <th className="py-2 pr-4 text-right">Loads</th>
                      <th className="py-2 pr-4 text-right">Load Volume</th>
                      <th className="py-2 pr-4 text-right">Redeems</th>
                      <th className="py-2 text-right">Redeem Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.by_game_provider || []).map((p) => (
                      <tr key={p.provider} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`game-provider-row-${p.provider}`}>
                        <td className="py-2.5 pr-4">
                          <span className="capitalize font-medium text-white">{p.provider}</span>
                        </td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-gray-300">{fmtInt(p.load_count)}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums font-semibold text-[#a78bfa]">{fmtMoney(p.load_amount)}</td>
                        <td className="py-2.5 pr-4 text-right tabular-nums text-gray-300">{fmtInt(p.redeem_count)}</td>
                        <td className="py-2.5 text-right tabular-nums font-semibold text-[#22c55e]">{fmtMoney(p.redeem_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const tooltipStyle = {
  background: '#0B0E14',
  border: '1px solid #ffffff15',
  borderRadius: 8,
  fontSize: 12,
};

function KPI({ icon: Icon, label, subtitle, primary, secondary, color, urgent, testid, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`bg-[#131822] rounded-2xl border p-4 ${urgent ? 'border-yellow-500/35' : 'border-white/10'} ${
        clickable ? 'cursor-pointer hover:border-[#D4AF37]/50 hover:bg-[#1a2030] transition-all active:scale-[0.99]' : ''
      }`}
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider flex items-center gap-1">
            {label}
            {clickable && <span className="text-[9px] text-gray-600">·</span>}
            {clickable && <span className="text-[9px] text-[#D4AF37]/60 uppercase">view →</span>}
          </p>
          <p className="text-xl font-bold text-white tabular-nums truncate" title={primary}>{primary}</p>
          {secondary && <p className="text-[10px] text-gray-400 tabular-nums">{secondary}</p>}
          {subtitle && !secondary && <p className="text-[10px] text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

function LifetimeStat({ icon: Icon, label, amount, count, color, testid }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid={testid}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <p className="text-2xl font-bold text-white tabular-nums truncate" title={amount}>{amount}</p>
        <p className="text-[10px] text-gray-500">{count}</p>
      </div>
    </div>
  );
}

function ProviderBreakdown({ icon: Icon, title, color, rows, amountPrefix = '', emptyLabel, testid }) {
  const total = (rows || []).reduce((acc, r) => acc + (r.amount || 0), 0);
  return (
    <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid={testid}>
      <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color }} /> {title}
      </h3>
      {(!rows || rows.length === 0) ? (
        <EmptyChart label={emptyLabel} />
      ) : (
        <div className="space-y-2">
          {rows.slice(0, 8).map((r) => {
            const pct = total > 0 ? ((r.amount || 0) / total * 100) : 0;
            return (
              <div key={r.provider} className="" data-testid={`provider-row-${r.provider}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-300 capitalize font-medium truncate">{formatProviderLabel(r.provider)}</span>
                  <span className="text-gray-400 tabular-nums flex-shrink-0 pl-2">
                    <span className="font-bold text-white">{amountPrefix}{fmtInt(r.amount || 0)}</span>
                    <span className="text-[10px] text-gray-500 ml-1">· {r.count || 0} txns</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color, opacity: 0.8 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatProviderLabel(key) {
  if (!key || key === 'unknown') return 'Unknown / direct';
  // "gateway_cashapp" → "Cash App" ; "linkgen_btc" → "BTC"
  return key
    .replace(/^gateway_/, '')
    .replace(/^linkgen_/, '')
    .replace(/_/g, ' ')
    .replace(/\bcashapp\b/gi, 'Cash App')
    .replace(/\bapplepay\b/gi, 'Apple Pay')
    .replace(/\bgooglepay\b/gi, 'Google Pay')
    .replace(/\bbtc\b/gi, 'BTC')
    .replace(/\bpaypal\b/gi, 'PayPal');
}

function EmptyChart({ label }) {
  return (
    <div className="flex items-center justify-center h-32 text-xs text-gray-500 border border-dashed border-white/10 rounded-xl">
      {label || 'No data available'}
    </div>
  );
}


function ProviderPoolBalances({ data, loading, onRefresh }) {
  const items = data?.items || [];
  const healthy = data?.healthy_providers ?? 0;
  const total = data?.total_providers ?? 0;
  const totalBalance = data?.total_balance_usd ?? 0;
  const fetchedAt = data?.fetched_at ? new Date(data.fetched_at) : null;

  // Flag providers with balance < $50 as low.
  const LOW_THRESHOLD = 50;
  const low = items.filter((i) => i.ok && typeof i.balance === 'number' && i.balance < LOW_THRESHOLD);

  return (
    <div
      className="rounded-2xl p-5 bg-gradient-to-br from-[#0f1a2e] to-[#0B0E14] border border-white/10"
      data-testid="provider-pool-balances"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#D4AF37]" /> Provider Pool Balances
          <span className="text-[10px] font-normal text-gray-500">
            · upstream game-provider accounts
          </span>
        </h3>
        <div className="flex items-center gap-3 text-[11px] text-gray-400">
          {fetchedAt && (
            <span>
              Updated {fetchedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 rounded-lg bg-[#0B0E14] border border-white/10 text-gray-400 hover:text-white hover:border-white/20 disabled:opacity-40 transition"
            data-testid="refresh-pool-balances"
            title="Refresh pool balances"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl p-3 bg-white/[0.02] border border-white/5" data-testid="pool-total-balance">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Combined Pool Balance</p>
          <p className="text-2xl font-bold text-[#22c55e] tabular-nums mt-0.5">
            ${Number(totalBalance).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5">across {healthy} healthy providers</p>
        </div>
        <div className="rounded-xl p-3 bg-white/[0.02] border border-white/5" data-testid="pool-healthy-count">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Healthy / Total</p>
          <p className="text-2xl font-bold text-white tabular-nums mt-0.5">
            {healthy} <span className="text-gray-500 text-base">/ {total}</span>
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5">providers responding</p>
        </div>
        <div className="rounded-xl p-3 bg-white/[0.02] border border-white/5" data-testid="pool-low-count">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Low Balance (&lt;${LOW_THRESHOLD})</p>
          <p className={`text-2xl font-bold tabular-nums mt-0.5 ${low.length ? 'text-amber-400' : 'text-gray-300'}`}>
            {low.length}
          </p>
          <p className="text-[10px] text-gray-500 mt-0.5">
            {low.length ? 'top-up required' : 'all providers comfortable'}
          </p>
        </div>
      </div>

      {loading && !items.length ? (
        <div className="flex items-center justify-center h-24 text-xs text-gray-500">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading pool balances…
        </div>
      ) : items.length === 0 ? (
        <EmptyChart label="No game providers configured" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((p) => {
            const isLow = p.ok && typeof p.balance === 'number' && p.balance < LOW_THRESHOLD;
            return (
              <div
                key={p.provider_code}
                className={`flex items-center gap-3 p-3 rounded-xl border ${
                  !p.ok
                    ? 'bg-red-500/5 border-red-500/20'
                    : isLow
                      ? 'bg-amber-500/5 border-amber-500/30'
                      : 'bg-white/[0.02] border-white/10'
                }`}
                data-testid={`pool-provider-${p.provider_code}`}
              >
                {p.logo_url ? (
                  <img
                    src={p.logo_url}
                    alt={p.provider_name}
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <Gamepad2 className="w-5 h-5 text-gray-500" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold text-white truncate">{p.provider_name}</p>
                    {p.ok ? (
                      isLow
                        ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                        : <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                    )}
                  </div>
                  {p.ok ? (
                    <p
                      className={`text-lg font-bold tabular-nums ${
                        isLow ? 'text-amber-400' : 'text-green-400'
                      }`}
                      data-testid={`pool-balance-${p.provider_code}`}
                    >
                      ${Number(p.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </p>
                  ) : (
                    <p
                      className="text-[11px] text-red-300 leading-tight line-clamp-2"
                      title={p.error}
                      data-testid={`pool-error-${p.provider_code}`}
                    >
                      {p.error || 'Unavailable'}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
